import {NumberFieldSettings} from "./NumberFieldSettings";
import {SwitchFieldSettings} from "./SwitchFieldSettings";

/**
 * ServiceFieldSettings содержит данные настроек полей для вывода в дополнительных
 * услугах.
 */
export class ServiceFieldSettings {
  /**
   * FieldBlockSize содержит размер блока для отображения поля.
   * Размер блока задается в колонках в диапазоне от 1 до 12.
   * Размер колонки вычисляется автоматически в зависимости от ширины контента.
   * Размер в 12 колонок означает 100% ширину относительно контента.
   */
  public FieldBlockSize: number = 2;

  /**
   * FieldWidth содержит ширину поля в блоке.
   * Размер поля задается в колонках в диапазоне от 1 до 12.
   * Размер колонки вычисляется автоматически в зависимости от ширины блока поля.
   * Размер в 12 колонок означает 100% ширину относительно блока.
   */
  public FieldWidth: number = 12;

  /**
   * FieldSize содержит размер поля в стилистике MUI.
   * Данное поле должно содержать одно из перечисленных значений:
   *   - small - Малый размер поля
   *   - normal - Нормальный размер поля
   */
  public FieldSize: string = "normal";

  /**
   * RowNumber содержит номер строки для отображения поля.
   * Поля выводятся построчно на основе общего пула значений этого поля.
   * Минимальное значение строки - 0.
   * Последовательность вывода - чем ниже значение, тем выше строка.
   * Пример: для заданных строк 1 и 2 первой будет выведена строка 1.
   */
  public RowNumber: number = 0;

  /**
   * PositionInRow содержит номер поля в выводимой строке.
   * Чем меньше значение, тем ближе к левому краю будет выводиться поле.
   * Минимальное значение позиции - 0.
   */
  public PositionInRow: number = 0;

  /**
   * IsFieldHidden содержит флаг скрытия поля. Скрытое поле участвует в
   * расчетах, а так же для него действуют правила авторасчета значения.
   * Скрытое поле исключается из вывода.
   */
  public IsFieldHidden: boolean = false;

  /**
   * IsNeedHideName содержит флаг, принудительно скрывающий вывод заголовка
   * поля, даже если он задан.
   */
  public IsNeedHideName: boolean = false;

  /**
   * IsNeedHidePlaceholder содержит флаг, принудительно скрывающий placeholder
   * поля, даже если он задан.
   */
  public IsNeedHidePlaceholder: boolean = false;

  /**
   * IsNeedDisplayOnlyTextInfo содержит флаг, позволяющий выводить поле в виде
   * исключительно текста. Подсказка к полю в этом случае выводится в виде знака
   * ? рядом с текстом (слева).
   */
  public IsNeedDisplayOnlyTextInfo: boolean = false;

  /**
   * IsNeedDisableField содержит флаг, позволяющий запретить редактирование поля
   * пользователем. Данный флаг не прерывает работу автоматического расчета поля.
   */
  public IsNeedDisableField: boolean = false;

  /**
   * Принудительно выводить поле в информации о товаре в корзине.
   * В блоке полей по умолчанию выводятся только поля виджета. Этот флаг позволяет
   * добавить вывод поля услуги.
   */
  public IsNeedShowFieldInCart: boolean = false;

  /**
   * FieldNameDirection содержит настройку выравнивания для названия поля.
   * Доступные варианты для выбора:
   *  - left - По левому краю
   *  - right - По правому краю
   *  - center - По центру
   */
  public FieldNameDirection: string = "left";

  /**
   * IsNeedDisplayFieldLeftText включающий отображение текста слева от поля
   * заданного в свойстве FieldLeftText
   */
  public IsNeedDisplayFieldLeftText: boolean = false;

  /**
   * FieldLeftText содежит локализованый текст для вывода слева от поля, при
   * условии включенного флага IsNeedDisplayFieldLeftText
   */
  public FieldLeftText: string = "";

  /**
   * IsAccordionEnabled включает отображение аккордеона и делает
   * достпуным группы для распределения под разнные аккордеоны
   */
  public IsAccordionEnabled: boolean = false;

  /**
   * IsAccordionDefaultCollapsed указывает состояние по умолчанию для аккордеона
   */
  public IsAccordionDefaultCollapsed: boolean = false;

  /**
   * AccordionGroupNumber указывает группу аккордеона, чтобы распределить поля
   * по разным аккордеонам
   */
  public AccordionGroupNumber: number = 0;

  /**
   * AccordionButtonText содержит текст для кнопки аккордеона, по которой будет
   * происходить разворачивание и сворачивание
   */
  public AccordionButtonText: string = "";

  /**
   * AccordionButtonText содержит текст для подсказки кнопки аккордеона, 
   * по которой будет происходить разворачивание и сворачивание, при наведении
   */
  public AccordionHelperText: string = "";

  /**
   * AccordionButtonBlockSize содержит размер блока для отображения кнопки аккордеона.
   * Размер блока задается в колонках в диапазоне от 1 до 12.
   * Размер колонки вычисляется автоматически в зависимости от ширины контента.
   * Размер в 12 колонок означает 100% ширину относительно контента.
   */
  public AccordionButtonBlockSize: number = 2;

  /**
	* AccordionButtonWidth содержит ширину кнопки аккордеона в блоке.
	* Размер поля задается в колонках в диапазоне от 1 до 12.
	* Размер колонки вычисляется автоматически в зависимости от ширины блока поля.
	* Размер в 12 колонок означает 100% ширину относительно блока.
	*/
 public AccordionButtonWidth: number = 12;

  /**
   * Дополнительные настройки полей различных типов
   */
  public NumberFieldSettings: NumberFieldSettings = NumberFieldSettings.RestoreSettings({});
  public HiddenFieldSettings: object = {};
  public SwitchFieldSettings: SwitchFieldSettings = SwitchFieldSettings.RestoreSettings({});
  public HandbookFieldSettings: object = {};
  public LocationFieldSettings: object = {};
  public TaxFieldSettings: object = {};
  public CurrencyFieldSettings: object = {};
  public CertificationContractorsFieldSettings: object = {};
  public ConsultingContractorsFieldSettings: object = {};
  public CustomsContractorsFieldSettings: object = {};
  public InspectionContractorsFieldSettings: object = {};
  public InsuranceContractorsFieldSettings: object = {};

  /**
   * Заглушка для запрета прямого создания объектов данного типа
   * @private
   */
  private constructor() {
  }

  /**
   * RestoreSettings выполняет восстановление данных объекта настроек
   * Функция выполняет нормализацию значений настроек.
   * Функция дополняет недостающие настройки значениями по умолчанию.
   *
   * @param data
   */
  public static RestoreSettings(data: any): ServiceFieldSettings {
    let result = new ServiceFieldSettings();

    if (typeof data === "string") {
      data = JSON.parse(data)
    }

    if (typeof data !== "object") {
      return result;
    }

    Object.keys(result).map((key: keyof ServiceFieldSettings) => {
      // @ts-ignore
      result[key] = ServiceFieldSettings.GetCorrectFieldValue(key, data[key])
    })

    return result;
  }

  /**
   * GetCorrectFieldValue выполняет восстановление данных поля из переданного значения настроек.
   * Данный метод так же проводит валидацию значения каждой из настроек поля. Метод возвращает
   * значение по умолчанию, если значение передано не корректно.
   */
  public static GetCorrectFieldValue<T extends keyof ServiceFieldSettings>(field: T, data: any): any {
    let defaults = new ServiceFieldSettings()

    if (data === undefined) {
      return defaults[field]
    }

    switch (field) {
      case "FieldBlockSize":
      case "FieldWidth":
	  case "AccordionButtonBlockSize":
	  case "AccordionButtonWidth":
        return typeof data === "number" && data >= 1 && data <= 12 ? data : defaults[field]
      case "FieldSize":
        return ["small", "normal"].indexOf(data) !== -1 ? data : defaults[field]
      case "FieldNameDirection":
        return ["left", "right", "center"].indexOf(data) !== -1 ? data : defaults[field]
      case "PositionInRow":
        return typeof data === "number" && data >= 0 ? data : defaults[field]
      case "RowNumber":
        return typeof data === "number" && data >= 0 ? data : defaults[field]
	  case "FieldLeftText":
	  case "AccordionHelperText":
	  case "AccordionButtonText":
		return typeof data === "string" ? data : defaults[field]
	  case "AccordionGroupNumber":
		return typeof data === "number" && data >= 0 ? data : defaults[field]
      case "IsFieldHidden":
      case "IsNeedHidePlaceholder":
      case "IsNeedHideName":
      case "IsNeedDisplayOnlyTextInfo":
      case "IsNeedDisableField":
      case "IsNeedShowFieldInCart":
	  case "IsNeedDisplayFieldLeftText":
	  case "IsAccordionEnabled":
	  case "IsAccordionDefaultCollapsed":
        return !!data
      case "NumberFieldSettings":
        return NumberFieldSettings.RestoreSettings(data)
      case "SwitchFieldSettings":
        return SwitchFieldSettings.RestoreSettings(data)
    }

    return defaults[field];
  }
}